import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import clsx from "clsx";
import { Button } from "./atoms";
import { ConfirmDeleteModal } from "./molecules/ConfirmDeleteModal";
import { deleteDepartment } from "../api/departments";

/**
 * Step 間で引き回す部門フォーム state（新規・編集共通）。
 * 現状ステップは「1. 基本情報」のみだが、将来の拡張に備えサイドメニュー構造を維持する。
 */
export interface DepartmentFormState {
    code: string;
    name: string;
    shortName: string;
}

export interface DepartmentFormOutletContext {
    setPageError: (message: string | null) => void;
    invalidFields: string[];
    setInvalidFields: (fields: string[]) => void;
    pendingFormState: DepartmentFormState | null;
    setPendingFormState: (state: DepartmentFormState | null) => void;
}

/** 「基本情報」タブに含まれる必須フィールドのキー一覧 */
const BASIC_INFO_REQUIRED_FIELDS = ["code", "name"] as const;

/** 部門コードの最大桁数 */
export const DEPARTMENT_CODE_MAX_LENGTH = 50;
/** 部門名の最大文字数 */
export const DEPARTMENT_NAME_MAX_LENGTH = 50;
/** 略称の最大文字数 */
export const DEPARTMENT_SHORT_NAME_MAX_LENGTH = 10;

const DEPARTMENT_CODE_PATTERN = /^[A-Za-z0-9]+$/;

/**
 * 部門コードの書式チェック（必須チェックは別途）
 * @returns エラーメッセージ。問題なければ null。
 */
export const validateDepartmentCode = (code: string): string | null => {
    if (code.length === 0) return null;
    if (code.length > DEPARTMENT_CODE_MAX_LENGTH) {
        return `部門コードは${DEPARTMENT_CODE_MAX_LENGTH}桁以内で入力してください`;
    }
    if (!DEPARTMENT_CODE_PATTERN.test(code)) {
        return "部門コードは半角英数字で入力してください";
    }
    return null;
};

/**
 * 部門名の文字数チェック（必須チェックは別途）
 */
export const validateDepartmentName = (name: string): string | null => {
    if (name.length === 0) return null;
    if (name.length > DEPARTMENT_NAME_MAX_LENGTH) {
        return `${DEPARTMENT_NAME_MAX_LENGTH}文字以内`;
    }
    return null;
};

/**
 * 略称の文字数チェック
 */
export const validateDepartmentShortName = (shortName: string): string | null => {
    if (shortName.length === 0) return null;
    if (shortName.length > DEPARTMENT_SHORT_NAME_MAX_LENGTH) {
        return `${DEPARTMENT_SHORT_NAME_MAX_LENGTH}文字以内`;
    }
    return null;
};

interface MenuItem {
    id: string;
    label: string;
    path: string;
    displayLabel: string;
}

const DepartmentEditLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;

    const [pageError, setPageError] = useState<string | null>(null);
    const [invalidFields, setInvalidFields] = useState<string[]>([]);
    const [pendingFormState, setPendingFormState] = useState<DepartmentFormState | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // 画面遷移時に子ページエラーを自動リセット
    useEffect(() => {
        setPageError(null);
    }, [location.pathname]);

    // 対象部門が変わったら state をリセット
    useEffect(() => {
        setPendingFormState(null);
        setInvalidFields([]);
    }, [id, isEdit]);

    const menuItems = useMemo<MenuItem[]>(() => {
        const base = isEdit ? `/org-settings/department/${id}` : "/org-settings/department/new";
        const basicPath = isEdit ? `${base}/edit` : base;
        return [
            { id: "1", label: "基本情報", path: basicPath, displayLabel: "1. 基本情報" },
        ];
    }, [id, isEdit]);

    const activeMenuId = "1";

    const handleDelete = async () => {
        if (!id) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteDepartment(parseInt(id, 10));
            navigate("/org-settings/department");
        } catch (err) {
            console.error("Failed to delete department:", err);
            setDeleteError(err instanceof Error ? err.message : "削除に失敗しました");
            setDeleting(false);
        }
    };

    // タブの赤表示: invalidFields に含まれ、かつ pendingFormState でも未入力の場合に true
    const hasBasicInfoError = useMemo(() => {
        const basicKeys = BASIC_INFO_REQUIRED_FIELDS as readonly string[];
        return invalidFields.some((f) => {
            if (!basicKeys.includes(f)) return false;
            if (!pendingFormState) return true;
            const value = pendingFormState[f as keyof DepartmentFormState];
            return !value || (typeof value === "string" && !value.trim());
        });
    }, [invalidFields, pendingFormState]);

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* ヘッダー */}
            <div className="px-6 py-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-6 bg-brand-500 rounded-full" />
                    <h1 className="text-xl font-bold text-gray-800">
                        部門の登録・編集
                    </h1>
                </div>
                {(deleteError || pageError) && (
                    <div className="mt-2 text-sm text-red-600">{deleteError ?? pageError}</div>
                )}
            </div>

            {/* フォームエリア */}
            <div className="flex-1 overflow-hidden px-6 pb-6">
                <div className="flex h-full gap-4">
                    {/* サイドメニュー */}
                    <div className="w-44 bg-white rounded-lg py-4 flex-shrink-0 flex flex-col">
                        <nav className="space-y-1 flex-1">
                            {menuItems.map((item) => {
                                const hasError = item.id === "1" && hasBasicInfoError;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => navigate(item.path)}
                                        className={clsx(
                                            "w-full text-left px-4 py-2 text-sm",
                                            hasError
                                                ? "text-red-600 font-medium"
                                                : activeMenuId === item.id
                                                    ? "text-brand-500 font-medium"
                                                    : "text-gray-600 hover:text-gray-900"
                                        )}
                                    >
                                        {item.displayLabel}
                                    </button>
                                );
                            })}
                        </nav>
                        {isEdit && (
                            <div className="px-4 pt-4 mt-auto">
                                <Button
                                    variant="danger"
                                    size="md"
                                    onClick={() => setShowDeleteModal(true)}
                                    disabled={deleting}
                                    className="w-full"
                                >
                                    削除
                                </Button>
                            </div>
                        )}
                    </div>

                    <ConfirmDeleteModal
                        isOpen={showDeleteModal}
                        onCancel={() => setShowDeleteModal(false)}
                        onConfirm={() => {
                            setShowDeleteModal(false);
                            handleDelete();
                        }}
                        isDeleting={deleting}
                        title="部門の削除"
                        message={
                            pendingFormState?.name
                                ? `部門「${pendingFormState.name}」を削除します。各店舗で使用部門として登録されているこの部門も合わせて解除されます。よろしいですか？`
                                : "この部門を削除します。各店舗で使用部門として登録されている同名部門も合わせて解除されます。よろしいですか？"
                        }
                    />

                    {/* タブごとの内容 */}
                    <Outlet context={{
                        setPageError,
                        invalidFields,
                        setInvalidFields,
                        pendingFormState,
                        setPendingFormState,
                    } satisfies DepartmentFormOutletContext} />
                </div>
            </div>
        </div>
    );
};

export default DepartmentEditLayout;
